import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Bell, Home, Calendar, Crown, Sparkles, User, Menu, X } from 'lucide-react';
import { WelcomeSplash } from './PuroAnimations';
import axios from 'axios';
import { CONTACT_EMAIL, INSTAGRAM_URL, FACEBOOK_URL } from '../config/site';
import { LegalFooter } from './LegalFooter';

import { API } from '../config/api';
const LOGO = "/assets/logo-puro.png";
const LOGO_FILTER = { filter: 'brightness(0) saturate(100%) invert(45%) sepia(28%) saturate(520%) hue-rotate(357deg) brightness(0.92)' };

const PUBLIC_NAV = [
  { label: 'Le Studio', to: '/#studio' },
  { label: "L'Univers PURO", to: '/univers' },
  { label: 'La Méthode', to: '/#methode' },
  { label: 'Coaching', to: '/#coaching' },
  { label: 'Forfaits', to: '/#forfaits' },
  { label: 'Inauguration', to: '/inauguration' },
  { label: 'Espace client', to: '/login' },
  { label: 'Contact', to: '/contact' },
];

export function Layout({ children }) {
  const { user, isAuthenticated, isCoach } = useAuth();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      const onUpdate = () => fetchUnreadCount();
      window.addEventListener('puro-notif-updated', onUpdate);
      return () => { clearInterval(interval); window.removeEventListener('puro-notif-updated', onUpdate); };
    }
  }, [isAuthenticated]);

  // Scroll vers l'ancre (#studio, #forfaits...) après navigation
  useEffect(() => {
    setMenuOpen(false);
    if (location.hash) {
      const t = setTimeout(() => {
        const el = document.getElementById(location.hash.slice(1));
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
      setUnreadCount(response.data.count);
    } catch (error) {}
  };

  const isActive = (path) => location.pathname === path;
  const blockedClient = isAuthenticated && !isCoach && user && (!user.chosen_plan || user.chosen_plan === 'single');
  const allowedWhileBlocked = ['/abonnement', '/profil', '/planning'].includes(location.pathname);
  const hideNav = ['/login', '/register'].includes(location.pathname) || (blockedClient && !allowedWhileBlocked);

  const clientNavItems = [
    { path: '/', icon: Home, label: 'Accueil' },
    { path: '/planning', icon: Calendar, label: 'Planning' },
    { path: '/club', icon: Crown, label: 'Club' },
    { path: '/abonnement', icon: Sparkles, label: 'Abonnement' },
    { path: '/profil', icon: User, label: 'Profil' },
  ];

  const coachNavItems = [
    { path: '/', icon: Home, label: 'Accueil' },
    { path: '/coach', icon: Calendar, label: 'Dashboard' },
    { path: '/profil', icon: User, label: 'Profil' },
  ];

  const coachOnlyNavItems = [
    { path: '/espace-coach', icon: Calendar, label: 'Mon espace Coach' },
    { path: '/planning', icon: Home, label: 'Planning studio' },
  ];

  const navItems = user?.role === 'admin' ? coachNavItems : user?.role === 'coach' ? coachOnlyNavItems : clientNavItems;

  return (
    <div className="min-h-screen bg-[#F5EFE5]">
      <WelcomeSplash />

      {/* Top Navigation */}
      {!hideNav && (
        <nav className="sticky top-0 z-50 glass border-b border-[#E9DDCC]/70">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
            <div className="flex items-center justify-between h-16 sm:h-[72px]">
              {/* Logo */}
              <Link to="/" className="flex items-center flex-shrink-0" data-testid="logo-link">
                <img src={LOGO} alt="PURO Studio Pilates Reformer" className="h-10 sm:h-11 w-auto" style={LOGO_FILTER} />
              </Link>

              {/* Nav centrale — visiteurs */}
              {!isAuthenticated && (
                <div className="hidden lg:flex items-center gap-7 absolute left-1/2 -translate-x-1/2">
                  {PUBLIC_NAV.map((item) => (
                    <Link key={item.label} to={item.to}
                      className="text-[13px] text-[#7A6455] hover:text-[#493329] transition-colors tracking-wide link-premium"
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s|é/g, (c) => c === 'é' ? 'e' : '-')}`}>
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}

              {/* Nav — membres connectées */}
              {isAuthenticated && (
                <div className="hidden md:flex items-center space-x-1">
                  {navItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`relative flex items-center space-x-2 px-4 py-2 rounded-full text-sm transition-all ${
                        isActive(item.path) ? 'text-[#493329] font-medium' : 'text-[#7A6455] hover:text-[#493329]'
                      }`}
                    >
                      <item.icon className={`w-4 h-4 ${isActive(item.path) ? 'text-[#C99A55]' : ''}`} strokeWidth={1.5} />
                      <span>{item.label}</span>
                      {isActive(item.path) && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#C99A55]" />}
                    </Link>
                  ))}
                </div>
              )}

              {/* Droite */}
              <div className="flex items-center gap-2">
                {isAuthenticated ? (
                  <Link to="/notifications" className="relative p-2" data-testid="notifications-link">
                    <Bell className="w-5 h-5 text-[#7A6455]" strokeWidth={1.5} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0 -right-0 w-4 h-4 bg-[#C99A55] rounded-full text-[10px] text-white flex items-center justify-center font-medium">
                        {unreadCount}
                      </span>
                    )}
                  </Link>
                ) : (
                  <>
                    <Link to="/register" className="hidden sm:block">
                      <Button className="bg-[#9A744C] hover:bg-[#493329] text-white rounded-full px-6 h-9 text-[13px] tracking-wide btn-premium" data-testid="nav-register-btn">
                        Préinscription
                      </Button>
                    </Link>
                    <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden p-2 text-[#493329]" data-testid="burger-menu-btn" aria-label="Menu">
                      {menuOpen ? <X className="w-6 h-6" strokeWidth={1.5} /> : <Menu className="w-6 h-6" strokeWidth={1.5} />}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Menu mobile — visiteurs */}
          {!isAuthenticated && menuOpen && (
            <div className="lg:hidden bg-[#F5EFE5] border-t border-[#E9DDCC] px-6 py-6 space-y-1 animate-fade-in" data-testid="mobile-menu">
              {PUBLIC_NAV.map((item) => (
                <Link key={item.label} to={item.to} onClick={() => setMenuOpen(false)}
                  className="block py-3 font-heading text-xl text-[#493329] border-b border-[#E9DDCC]/60">
                  {item.label}
                </Link>
              ))}
              <div className="pt-5 space-y-2.5">
                <Link to="/register" onClick={() => setMenuOpen(false)} className="block">
                  <Button className="w-full bg-[#9A744C] hover:bg-[#493329] text-white rounded-md h-11 text-sm" data-testid="mobile-register-btn">Je me préinscris</Button>
                </Link>
                <Link to="/login" onClick={() => setMenuOpen(false)} className="block">
                  <Button variant="outline" className="w-full border-[#D7C4AA] text-[#493329] rounded-md h-11 text-sm bg-transparent">Espace client</Button>
                </Link>
              </div>
            </div>
          )}
        </nav>
      )}

      {/* Main Content */}
      <main className={isAuthenticated && !hideNav ? 'pb-20 md:pb-0' : ''}>
        {children}
      </main>

      {/* Bottom Navigation (Mobile - Authenticated) */}
      {isAuthenticated && !hideNav && (
        <nav className="md:hidden fixed bottom-0 left-0 right-0 glass border-t border-[#E9DDCC] z-50">
          <div className="flex items-center justify-around h-16 px-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center flex-1 py-2 ${
                  isActive(item.path) ? 'text-[#C99A55]' : 'text-[#7A6455]'
                }`}
              >
                <item.icon className="w-5 h-5 mb-1" strokeWidth={1.5} />
                <span className="text-[10px] font-medium">{item.label}</span>
              </Link>
            ))}
          </div>
        </nav>
      )}

      {/* Footer — public */}
      {!isAuthenticated && ['/', '/univers'].includes(location.pathname) && (
        <footer className="bg-[#F5EFE5] border-t border-[#E9DDCC]" data-testid="footer">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-10">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
              <Link to="/" className="flex-shrink-0">
                <img src={LOGO} alt="PURO Studio Pilates Reformer" className="h-12 w-auto" style={LOGO_FILTER} />
              </Link>

              <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
                {PUBLIC_NAV.map((item) => (
                  <Link key={item.label} to={item.to} className="text-xs text-[#7A6455] hover:text-[#493329] transition-colors tracking-wide">
                    {item.label}
                  </Link>
                ))}
              </nav>

              <div className="flex items-center gap-5">
                <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="text-[#AE9885] hover:text-[#9A744C] transition-colors" data-testid="social-instagram" aria-label="Instagram">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                </a>
                <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="text-[#AE9885] hover:text-[#9A744C] transition-colors" data-testid="social-facebook" aria-label="Facebook">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <span className="text-xs text-[#AE9885] tracking-wide">À très bientôt à Magalas ♥</span>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-[#E9DDCC]/60 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-[11px] text-[#AE9885]">
              <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-[#9A744C] transition-colors">{CONTACT_EMAIL}</a>
              <span className="hidden sm:inline">·</span>
              <span>@puropilatesreformer</span>
              <span className="hidden sm:inline">·</span>
              <span>puropilatesreformer.com</span>
            </div>
          </div>
        </footer>
      )}
      <LegalFooter />
    </div>
  );
}
